import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

/** Tenant sidebar/navbar vurgu renkleri */
export const TENANT_CHROME = {
  accent: '#00d4aa',
  warning: '#f59e0b',
  danger: '#ef4444',
} as const

/** Tenant chrome — nötr zemin; sidebar/navbar/sayfa içi üç ayrı kart */
export function getTenantChromeColors(theme: Theme) {
  const isDark = theme.palette.mode === 'dark'
  if (isDark) {
    return {
      isDark,
      bgDark: '#0a0a0a',
      bgCard: '#141414',
      accentGlow: 'rgba(0, 212, 170, 0.12)',
      warningGlow: 'rgba(245, 158, 11, 0.18)',
      borderColor: 'rgba(255, 255, 255, 0.08)',
      shellBorderColor: 'rgba(255, 255, 255, 0.12)',
      insetBg: 'rgba(255, 255, 255, 0.04)',
      navHoverBg: 'rgba(255, 255, 255, 0.06)',
      navActiveBg: 'rgba(0, 212, 170, 0.12)',
      actionBg: 'rgba(255, 255, 255, 0.06)',
      actionHoverBg: 'rgba(255, 255, 255, 0.1)',
      textPrimary: '#f5f5f5',
      textSecondary: 'rgba(255, 255, 255, 0.58)',
      accent: TENANT_CHROME.accent,
      warning: TENANT_CHROME.warning,
    }
  }
  return {
    isDark,
    bgDark: '#dce4ef',
    bgCard: 'rgba(255, 255, 255, 0.97)',
    accentGlow: 'rgba(0, 212, 170, 0.1)',
    warningGlow: 'rgba(245, 158, 11, 0.12)',
    borderColor: 'rgba(15, 23, 42, 0.09)',
    shellBorderColor: 'rgba(15, 23, 42, 0.14)',
    insetBg: 'rgba(15, 23, 42, 0.03)',
    navHoverBg: 'rgba(15, 23, 42, 0.04)',
    navActiveBg: 'rgba(0, 212, 170, 0.08)',
    actionBg: 'rgba(15, 23, 42, 0.04)',
    actionHoverBg: 'rgba(15, 23, 42, 0.07)',
    textPrimary: '#0f172a',
    textSecondary: 'rgba(15, 23, 42, 0.55)',
    accent: '#059669',
    warning: TENANT_CHROME.warning,
  }
}

export const TENANT_SIDEBAR_WIDTH = 260
export const TENANT_SIDEBAR_COLLAPSED_WIDTH = 80
/** Genel dış boşluk (üst/sağ/alt) */
export const TENANT_CHROME_GAP = 1
/** Sidebar ile navbar/sayfa içi arası — daha dar */
export const TENANT_CHROME_GAP_SIDEBAR_EDGE = 0.5

/** Tenant scroll alanları: kaydırma var, çubuk görünmez */
export const tenantChromeHiddenScrollSx = {
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const

export type TenantChromeColors = ReturnType<typeof getTenantChromeColors>

/** Sidebar, navbar ve sayfa içi ortak yüzey */
export function tenantChromeCardSurface(chrome: TenantChromeColors) {
  return {
    borderRadius: 4,
    bgcolor: chrome.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${chrome.borderColor}`,
  } as const
}

/** Kenar çubuğu ve üst çubuk — hafif ama belirgin çerçeve */
export function tenantChromeShellSurface(chrome: TenantChromeColors) {
  return {
    borderRadius: 4,
    bgcolor: chrome.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${chrome.shellBorderColor}`,
    boxShadow: chrome.isDark
      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.05)'
      : 'inset 0 1px 0 rgba(255, 255, 255, 0.85)',
  } as const
}

export type TenantContentColors = ReturnType<typeof getTenantContentColors>

/** Dashboard ve iç sayfa panelleri */
export function getTenantContentColors(theme: Theme) {
  const isDark = theme.palette.mode === 'dark'
  return {
    isDark,
    text: isDark ? '#f5f5f5' : theme.palette.text.primary,
    textSoft: isDark ? alpha('#fff', 0.82) : theme.palette.text.secondary,
    textMuted: isDark ? alpha('#fff', 0.58) : alpha(theme.palette.text.primary, 0.5),
    accent: isDark ? '#34ebc4' : TENANT_CHROME.accent,
    warning: TENANT_CHROME.warning,
    panelBg: isDark ? '#1e1e1e' : '#ffffff',
    panelBorder: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.16)',
    panelShadow: isDark
      ? '0 6px 24px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
      : '0 2px 6px rgba(15, 23, 42, 0.07), 0 10px 28px rgba(15, 23, 42, 0.1)',
    tileBg: isDark ? '#242424' : '#ffffff',
    tileBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.14)',
    tileShadow: isDark
      ? '0 3px 14px rgba(0, 0, 0, 0.28)'
      : '0 2px 10px rgba(15, 23, 42, 0.08)',
  }
}

export function tenantContentPanelSx(c: TenantContentColors, extra?: object) {
  return {
    borderRadius: '12px',
    border: `1.5px solid ${c.panelBorder}`,
    bgcolor: c.panelBg,
    boxShadow: c.panelShadow,
    color: c.text,
    ...extra,
  } as const
}

/** Chrome kartı içindeki standart liste/ayar sayfası gövdesi — ayrı zemin rengi yok */
export function tenantPageLayoutSx(extra?: object) {
  return {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 1, sm: 1.25 },
    pt: { xs: 2, sm: 3 },
    pb: { xs: 1.5, sm: 2 },
    ...extra,
  } as const
}
